import Image from 'next/image';
import Layout from '../../ui/layout';
import { Wrap, Button } from '../../styled';
import { TypeformButtonMain } from '../../components';
import {
  H3,
  Ul,
  Intro,
  IntroInner,
  IntroText,
  Sizing,
  SizingItems,
  SizingItem,
  Features,
  FeaturesInner,
  FeaturesCell,
  Benefits,
  BenefitsInner,
  BenefitsCell,
  Integrations,
  IntegrationsItems,
  IntegrationsItem,
  Omnichannel,
  OmnichannelItemTitle,
  Demo,
  DemoInner,
} from './styled';

import howWeGetTheRightSizing1 from './images/how-we-get-the-right-sizing-1.svg';
import howWeGetTheRightSizing2 from './images/how-we-get-the-right-sizing-2.svg';
import howWeGetTheRightSizing3 from './images/how-we-get-the-right-sizing-3.svg';
import howWeGetTheRightSizing4 from './images/how-we-get-the-right-sizing-4.svg';
import benefits from './images/benefits.jpg';
import integrations1 from './images/integrations-1.svg';
import integrations2 from './images/integrations-2.svg';
import integrations3 from './images/integrations-3.svg';
import integrations4 from './images/integrations-4.svg';
import omnichannel1 from './images/omnichannel-1.svg';
import omnichannel2 from './images/omnichannel-2.svg';
import omnichannel3 from './images/omnichannel-3.svg';
import omnichannel4 from './images/omnichannel-4.svg';

const HowItWorks = () => (
  <Layout>
    <Intro>
      <Wrap>
        <IntroInner>
          <IntroText>
            Using Machine Learning and Advanced Analytics, we empower apparel
            brands and retailers
          </IntroText>
        </IntroInner>
      </Wrap>
    </Intro>
    <Sizing>
      <Wrap>
        <H3>How We Get the Sizing Right</H3>
        <SizingItems>
          <SizingItem>
            <Image
              src={howWeGetTheRightSizing1}
              width="192"
              height="189"
              alt=""
            />
            <p>Your customers share their personal information</p>
          </SizingItem>
          <SizingItem>
            <Image
              src={howWeGetTheRightSizing2}
              width="192"
              height="189"
              alt=""
            />
            <p>Our multi-parameter algorithm supports numerous measurements</p>
          </SizingItem>
          <SizingItem>
            <Image
              src={howWeGetTheRightSizing3}
              width="192"
              height="189"
              alt=""
            />
            <p>
              We have the largest statistical database of body measurements by
              geographical region
            </p>
          </SizingItem>
          <SizingItem>
            <Image
              src={howWeGetTheRightSizing4}
              width="192"
              height="189"
              alt=""
            />
            <p>Our patented algorithm is powered by Machine Learning</p>
          </SizingItem>
        </SizingItems>
      </Wrap>
    </Sizing>
    <Features>
      <Wrap>
        <FeaturesInner>
          <FeaturesCell>
            <H3>Features</H3>
            <Ul>
              <li>
                Detailed measurements can be personalised by each shopper. Match
                user size and fit preferences using intuitive visualization
              </li>
              <li>
                Multiple profiles enable your customers to shop for others
              </li>
              <li>
                Access to a state-of-the-art interface platform for apparel
                brands and fashion retailers
              </li>
              <li>
                Omnichannel user experience with in-store ContactlessSizing™
                enables sizing support for shop assistants, smart mirror
                compatibility, and a QR code integration to the retailer’s app
              </li>
              <li>3D Body scan compatible</li>
            </Ul>
          </FeaturesCell>
          <FeaturesCell>
            <video
              src="/images/how-it-works/features.mp4"
              autoPlay
              loop
              muted
              playsInline
              style={{ width: '100%' }}
            />
          </FeaturesCell>
        </FeaturesInner>
      </Wrap>
    </Features>
    <Benefits>
      <Wrap>
        <BenefitsInner>
          <BenefitsCell>
            <H3>Benefits</H3>
            <Ul>
              <li>
                Collect insightful data from across all channels on your
                customer’s body measurements, fit preferences and garment
                choices
              </li>
              <li>
                Turn data into actionable insights for smarter marketing,
                inventory management, cross-selling and manufacturing
              </li>
              <li>Increase your conversion rate and basket size</li>
              <li>
                Provide a more personalised experience for the shopper, both
                online and in-store
              </li>
            </Ul>
          </BenefitsCell>
          <BenefitsCell>
            <Image src={benefits} width="663" height="584" alt="" />
          </BenefitsCell>
        </BenefitsInner>
      </Wrap>
    </Benefits>
    <Integrations>
      <Wrap>
        <H3>Integrations</H3>
        <p>
          EyeFitU’s SizeEngine™ seamlessly integrates into all e-commerce
          platforms and POS systems
        </p>
        <IntegrationsItems>
          <IntegrationsItem>
            <Image src={integrations1} width="105" height="121" alt="" />
            <p>Send us your size guides in any format</p>
          </IntegrationsItem>
          <IntegrationsItem>
            <Image src={integrations2} width="105" height="121" alt="" />
            <p>We provide you with a script to add on your website</p>
          </IntegrationsItem>
          <IntegrationsItem>
            <Image src={integrations3} width="105" height="121" alt="" />
            <p>You test and we do the quality assurance before going live</p>
          </IntegrationsItem>
          <IntegrationsItem>
            <Image src={integrations4} width="105" height="121" alt="" />
            <p>
              You track results as we provide you with our advanced analytics
            </p>
          </IntegrationsItem>
        </IntegrationsItems>
      </Wrap>
    </Integrations>
    <Omnichannel>
      <Wrap>
        <H3>Omnichannel User Experience</H3>
        <p>
          The sizing solution that helps your clients find the right fit across
          all channels, while providing you with a 360° customer view.
        </p>
        <IntegrationsItems>
          <IntegrationsItem>
            <Image src={omnichannel1} width="105" height="121" alt="" />
            <OmnichannelItemTitle>In-store</OmnichannelItemTitle>
            <p>
              By encouraging your customers to create size profiles when
              shopping in-store, you foster a more personalized relationship.
            </p>
          </IntegrationsItem>
          <IntegrationsItem>
            <Image src={omnichannel2} width="105" height="121" alt="" />
            <OmnichannelItemTitle>Clientelling</OmnichannelItemTitle>
            <p>
              Our in-store sizing support enables shop assistants to provide
              hands-on clientelling on a tablet, desktop or mobile app.
            </p>
          </IntegrationsItem>
          <IntegrationsItem>
            <Image src={omnichannel3} width="105" height="121" alt="" />
            <OmnichannelItemTitle>Smart Mirror</OmnichannelItemTitle>
            <p>
              Our system is fully compatible with smart mirrors. Offer size
              recommendations to your customers in fitting rooms.
            </p>
          </IntegrationsItem>
          <IntegrationsItem>
            <Image src={omnichannel4} width="105" height="121" alt="" />
            <OmnichannelItemTitle>QR Code</OmnichannelItemTitle>
            <p>
              Our QR code reader integration into retailer apps enables your
              customers to create their own size profile in-store as they shop.
            </p>
          </IntegrationsItem>
        </IntegrationsItems>
      </Wrap>
    </Omnichannel>
    <Demo>
      <Wrap>
        <DemoInner>
          <div>
            <H3>
              See Examples of
              <br />
              Our Work
            </H3>
            <Button as={TypeformButtonMain}>Learn more</Button>
          </div>
        </DemoInner>
      </Wrap>
    </Demo>
  </Layout>
);

export default HowItWorks;
